const SEARCH_BATCH_SIZE = 20;

class OrderImporter extends HTMLElement {
  constructor() {
    super();
    // Import button replaced by file input - upload triggers on file selection
    // this.querySelector('button[data-action="import"]')?.addEventListener('click', () => this.handleUpload());
    this.querySelectorAll('button[data-action="add-to-cart"]').forEach(button => {
      button.addEventListener('click', () => this.handleAddToCart());
    });
    this.querySelectorAll('button[data-action="add-all-to-cart"]').forEach(button => {
      button.addEventListener('click', (evt) => this.handleAddAllToCart(evt));
    });
    this.handleRemoveLine();
    this.initFileInput();

    this.isUploading = false;

    this.irPopup = document.querySelector('ir-popup');

    /** Show message in ir-popup (info) or fallback to alert if popup not available. */
    this._showInfo = (message, title = '') => {
      if (this.irPopup?.show) {
        this.irPopup.show({ type: 'info', title, message });
      } else {
        alert(message);
      }
    };

    // Pagination state
    this.currentPage = 1;
    this.itemsPerPage = 40;
    this.totalPages = 0;
    this.allProductSkus = [];
    this.allInputSkuQty = {};
    
    this.addEventListener('click', (event) => {
      if (event.target.matches('[data-action="add-item-to-cart"]')) {
        this.handleAddItemToCart(event);
      }
      // Handle pagination clicks
      if (event.target.matches('[data-action="pagination"]')) {
        this.handlePaginationClick(event);
      }
    });
  }

  initFileInput() {
    const fileInput = this.querySelector('#fileInput');
    const fileInputLabel = this.querySelector('label[for="fileInput"]');
    const fileInputName = this.querySelector('#fileInputName');

    if (fileInput && fileInputName) {
      fileInput.addEventListener('change', () => {
        // Prevent concurrent uploads
        if (this.isUploading) {
          fileInput.value = '';
          return;
        }
        const fileName = fileInput.files[0]?.name;
        fileInputName.textContent = fileName || (window.orderImporterStrings?.no_file_selected || 'No file selected');
        // Auto-trigger upload when file is selected
        if (fileInput.files[0]) {
          this.handleUpload();
        }
      });
    }

    // Handle keyboard accessibility for the label
    if (fileInputLabel) {
      fileInputLabel.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          fileInput?.click();
        }
      });
    }
  }

  async handleUpload() {
    // Prevent concurrent uploads
    if (this.isUploading) return;
    this.isUploading = true;

    const input = this.querySelector('input[type="file"]');
    const file = input.files[0];
    const inputSkuQty = {}
    let productSkus = [];

    if (!file) {
      this._showInfo(window.orderImporterStrings.missing_file_message || 'Please select a file to upload.');
      this.isUploading = false;
      return;
    } else {
      try {
        const csvText = await file.text();
        const lines = csvText.split('\n').map(line => line.trim()).filter(Boolean);
        const errorLines = [];
        const products = lines.map(line => {
          let lineParts = line.split(',');
          if (lineParts.length != 4) {
            errorLines.push(line);
            return null; // Skip invalid lines
          }
          const [index, tempField, sku, qty] = lineParts;
          return {
            index: parseInt(index),
            tempField: tempField.trim(),
            sku: sku.trim(),
            qty: parseInt(qty)
          };
        });
        productSkus = products.map(product => product.sku).filter(Boolean);
        products.forEach(product => {
          inputSkuQty[product.sku] = product.qty;
        });
      } catch (error) {
        this._showInfo(window.orderImporterStrings.file_format_invalid || 'File format is invalid');
        this.isUploading = false;
        return;
      }
    }

    if (!productSkus.length) {
      this._showInfo(window.orderImporterStrings.no_barcode_found || 'No valid product SKUs found.');
      this.isUploading = false;
      return;
    }

    if (productSkus.length > 500) {
      this._showInfo(window.orderImporterStrings.maximum_barcodes_limit_reached || 'Maximum SKUs limit reached.');
      this.isUploading = false;
      return;
    }

    // Store all SKUs and quantities for pagination
    this.allProductSkus = productSkus;
    this.allInputSkuQty = inputSkuQty;
    this.currentPage = 1;
    this.totalPages = Math.ceil(productSkus.length / this.itemsPerPage);
    
    // Load the first page (don't scroll on initial load)
    this.loadPage(this.currentPage, false, true);
  }

  loadPage(pageNumber, shouldScroll = true, isInitialUpload = false) {
    if (pageNumber < 1 || pageNumber > this.totalPages) {
      if (isInitialUpload) this.isUploading = false;
      return;
    }
    
    this.currentPage = pageNumber;
    
    // Calculate the range of SKUs for this page
    const startIndex = (pageNumber - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const currentPageSkus = this.allProductSkus.slice(startIndex, endIndex);
    
    if (!currentPageSkus.length) {
      if (isInitialUpload) this.isUploading = false;
      return;
    }

    this.querySelector('button[data-action="import"]')?.classList.add('is-loading');
    this.querySelector('label[for="fileInput"]')?.classList.add('is-loading');
    
    const searchBatches = [];
    for (let i = 0; i < currentPageSkus.length; i += SEARCH_BATCH_SIZE) {
      searchBatches.push(currentPageSkus.slice(i, i + SEARCH_BATCH_SIZE));
    }
    Promise.all(searchBatches.map(batch => {
      const searchQuery = batch.map(sku => `variants.sku:${sku}`).join(' OR ');
      return fetch(`/search?q=${encodeURIComponent(searchQuery)}&type=product&view=order-importer&options[prefix]=none`)
        .then(response => response.json());
    }))
      .then(responses => responses.flat())
      .then(data => {
        let tableData = [];
        
        if (!this.productMap) this.productMap = new Map();
        const productMap = this.productMap;
        if (data.length) {
          data.forEach(item => {
            item.variants.forEach(variant => {
              if (variant.sku && currentPageSkus.includes(variant.sku)) {
                productMap.set(variant.sku, {
                  item: item,
                  variant: variant
                });
              }
            });
          });
        }
        
        // Process each SKU in the current page (this ensures we show exactly xx lines)
        currentPageSkus.forEach(sku => {
          const quantityToAdd = this.allInputSkuQty[sku] || 1;
          
          if (productMap.has(sku)) {
            // Found product - create normal row
            const { item, variant } = productMap.get(sku);
            const title = item.title;
            const image = variant.featuredImage || (item.featuredImage || '');
            // const statusClass = variant.available ? 'available' : 'unavailable';
            const id = variant.id;
            const productId = item.id;
            // console.log('variant', item);
            const variantUrl = `/products/${item.handle}?variant=${id}`;
            const price = variant.priceWithCurrency ? variant.priceWithCurrency : '';
            const priceClass = variant.price == 0 ? 'hidden' : '';
            let minQty = variant.quantityRule?.min || 1;
            let maxQty = variant.quantityRule?.max || null;
            let incrementQty = variant.quantityRule?.increment || 1;
            let inputQty = quantityToAdd
            if (!OrderImporter.isOrderableInOp(item)) {
              variant.available = false;
            }
            if (!variant.available) {
              minQty = 0;
              inputQty = 0;

            }
            if (inputQty < minQty) {
              inputQty = minQty;
            }
            if (maxQty && inputQty > maxQty) {
              inputQty = maxQty;
            }

            let priceHtml = `<div class="op-product-price ${priceClass}">${price.replace(' kr', '')}</div>`;
            if (variant.quantityPriceBreaks.length) {
              priceHtml += `<div class="op-product-price-breaks">`;
              variant.quantityPriceBreaks.forEach(quantityPriceBreak => {
                priceHtml += `<div class="op-product-price-break">
                  ${quantityPriceBreak.minimum_quantity}+ ${window.orderImporterStrings.pcs || 'stk'}: ${quantityPriceBreak.priceWithCurrency}
                </div>`;
              });
              priceHtml += `</div>`;
            }

            let quantityHtml = `<quantity-input class="inline-block ${variant.available ? '' : 'op-quantity-not-found'}">
                <div class="qty-input qty-input--combined inline-flex items-center w-full">
                  <button type="button" class="qty-input__btn btn btn--minus no-js-hidden" name="minus" aria-controls="op-oi-vid-${id}">
                    <span class="visually-hidden">Decrease quantity</span>
                  </button>
                  <input type="number" class="qty-input__input input" id="op-oi-vid-${id}" name="quantity" min="${minQty}" step="${incrementQty}" ${maxQty ? `max="${maxQty}"` : ''} value="${variant.available ? inputQty : 0}" data-vid="${id}" data-available="${variant.available}">
                  <button type="button" class="qty-input__btn btn btn--plus no-js-hidden" name="plus" aria-controls="op-oi-vid-${id}">
                    <span class="visually-hidden">Increase quantity</span>
                  </button>
                </div>
              </quantity-input>`;
            if (quantityToAdd < minQty) {
              quantityHtml += `<p>${window.orderImporterStrings.minimum_quantity || 'Minimum quantity'}: ${minQty}</p>`;
            }
            if (maxQty && quantityToAdd > maxQty) {
              quantityHtml += `<p>${window.orderImporterStrings.maximum_quantity || 'Maximum quantity'}: ${maxQty}</p>`;
            }
            if (variant.quantityRule?.increment && variant.quantityRule?.increment > 1) {
              quantityHtml += `<p>${window.orderImporterStrings.pack_of || 'Package of'}: ${variant.quantityRule?.increment}</p>`;
            }

            let prodDetailsHtml = `
              <div class="op-prod-details flex flex-wrap gap-3">
                <img src="${image}" alt="" class="op-product-image" />
                <div>
                  <a href="${variantUrl}" title="" target="_blank">${title}</a>
                </div>
              </div>
            `;
            let stockStatusHtml = `<div class="text-sm inventory-store-customer hidden" data-product-id="${productId}" data-variant-sku="${variant.sku}" data-next-arrival-date="${item.metafields.nextArrivalDate || ''}">
              <div class="flex flex-wrap gap-1"><b>IR:</b> <span class="inventory-store-ir-value inventory-store-value out-stock">0 / 0</span></div>
              <div class="flex flex-wrap gap-1"><b>Butik:</b> <span class="inventory-store-customer-value inventory-store-value out-stock">0 / 0 / 0</span></div>
            </div>`;
            let actionHtml = `
              <button class="btn btn--primary" data-action="add-item-to-cart" ${variant.available ? '' : 'disabled'}>
                ${variant.available ? window.orderImporterStrings.add_to_cart : window.orderImporterStrings.sold_out_text}
              </button>
              <a href="#" data-vid="${id}" class="op-product-remove" data-sku="${sku}">
                <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation" class="icon">
                <path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5
                 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"></path>
                <path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z" fill="currentColor"></path>
                </svg>
              </a>`;

            let barcodeAndSkuHtml = `
              <div class="op-barcode-and-sku flex flex-col">
                <span class="op-sku">${sku}</span>  
                <span class="op-barcode">${variant.barcode || ''}</span>
              </div>
            `;
            tableData.push([prodDetailsHtml, barcodeAndSkuHtml, priceHtml, stockStatusHtml, quantityHtml, actionHtml]);
            
          } else {
            // Not found product - create disabled row
            let prodDetailsHtml = `
              <div class="op-prod-details op-prod-details--not-found flex flex-wrap gap-3">
                <div class="op-product-placeholder">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21,15 16,10 5,21"/>
                  </svg>
                </div>
                <div>
                  <span class="op-product-not-found-title">${window.orderImporterStrings.product_not_found || 'Product not found'}</span>
                </div>
              </div>
            `;
            let priceHtml = `<div class="op-product-price">-</div>`;
            let stockStatusHtml = ``;
            let quantityHtml = `
              <quantity-input class="inline-block op-quantity-not-found">
                <div class="qty-input qty-input--combined inline-flex items-center w-full">
                  <button type="button" class="qty-input__btn btn btn--minus no-js-hidden" name="minus" >
                    <span class="visually-hidden">Decrease quantity</span>
                  </button>
                  <input type="number" class="qty-input__input input" id="op-oi-vid-" name="quantity" value="0">
                  <button type="button" class="qty-input__btn btn btn--plus no-js-hidden" name="plus" >
                    <span class="visually-hidden">Increase quantity</span>
                  </button>
                </div>
              </quantity-input>
            `;
            let actionHtml = `
              <div class="op-not-found-actions">
                <button class="btn btn--primary op-not-available-text" data-action="add-item-to-cart" disabled>
                  ${window.orderImporterStrings.unavailable_text || 'Unavailable'}
                </button>
                <a href="#" data-sku="${sku}" class="op-product-remove">
                  <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation" class="icon">
                  <path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5
                   0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"></path>
                  <path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z" fill="currentColor"></path>
                  </svg>
                </a>
              </div>
            `;

            let barcodeAndSkuHtml = `
              <div class="op-barcode-and-sku flex flex-col">
                <span class="op-sku">${sku}</span>  
                <span class="op-barcode">-</span>
              </div>
            `;
            
            tableData.push([prodDetailsHtml, barcodeAndSkuHtml, priceHtml, stockStatusHtml, quantityHtml, actionHtml]);
          }
        });

        // Display table - now we always have exactly currentPageSkus.length rows
        const table = this.querySelector('#op-order-importer-table');
        if (table && tableData.length && typeof table.setRows === 'function') {
          table.classList.remove('hidden');
          table.setRows(tableData);
          // render stock status
          document.dispatchEvent(new CustomEvent('ir:stock-references:fetch', {}));
          this.querySelectorAll('button[data-action="add-to-cart"]').forEach(button => {
            button.disabled = false;
          });
          this.querySelectorAll('button[data-action="add-all-to-cart"]').forEach(button => {
            button.disabled = false;
          });
        }
        
        // Update pagination UI after loading data
        this.updatePaginationUI();
        
        // Scroll to top of the page if navigating via pagination
        if (shouldScroll) {
          this.scrollToTop();
        }
      })
      .finally(() => {
        this.querySelector('button[data-action="import"]')?.classList.remove('is-loading');
        this.querySelector('label[for="fileInput"]')?.classList.remove('is-loading');
        if (isInitialUpload) {
          this.isUploading = false;
        }
      });
  }

  updatePaginationUI() {
    let paginationContainer = this.querySelector('.op-pagination');
    
    // Create pagination container if it doesn't exist
    if (!paginationContainer) {
      paginationContainer = document.createElement('div');
      paginationContainer.classList.add('op-pagination');
      
      // Insert after the table
      const table = this.querySelector('#op-order-importer-table');
      if (table && table.parentNode) {
        table.parentNode.insertBefore(paginationContainer, table.nextSibling);
      }
    }
    
    if (this.totalPages <= 1) {
      paginationContainer.innerHTML = '';
      return;
    }
    
    const startItem = (this.currentPage - 1) * this.itemsPerPage + 1;
    const endItem = Math.min(this.currentPage * this.itemsPerPage, this.allProductSkus.length);
    
    let paginationHTML = `
      <div class="op-pagination-info">
        Showing ${startItem}-${endItem} of ${this.allProductSkus.length} products
      </div>
      <div class="op-pagination-controls">
        <button class="btn btn--secondary" data-action="pagination" data-page="prev" ${this.currentPage === 1 ? 'disabled' : ''}>
          Previous
        </button>
    `;
    
    // Always show first page
    if (this.totalPages > 1) {
      paginationHTML += `
        <button class="btn ${1 === this.currentPage ? 'btn--primary' : 'btn--secondary'}" 
                data-action="pagination" data-page="1" ${1 === this.currentPage ? 'disabled' : ''}>
          1
        </button>
      `;
    }
    
    // Show ellipsis after first page if needed
    if (this.currentPage > 4 && this.totalPages > 6) {
      paginationHTML += `<span class="op-pagination-ellipsis">...</span>`;
    }
    
    // Show page numbers around current page
    const maxVisiblePages = 3; // Reduced to make room for first/last
    let startPage = Math.max(2, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages - 1, startPage + maxVisiblePages - 1);
    
    // Adjust range to avoid overlapping with first/last
    if (startPage <= 2) {
      startPage = 2;
      endPage = Math.min(this.totalPages - 1, startPage + maxVisiblePages - 1);
    }
    if (endPage >= this.totalPages - 1) {
      endPage = this.totalPages - 1;
      startPage = Math.max(2, endPage - maxVisiblePages + 1);
    }
    
    // Only show middle pages if they don't overlap with first/last
    if (startPage <= endPage && startPage > 1 && endPage < this.totalPages) {
      for (let i = startPage; i <= endPage; i++) {
        paginationHTML += `
          <button class="btn ${i === this.currentPage ? 'btn--primary' : 'btn--secondary'}" 
                  data-action="pagination" data-page="${i}" ${i === this.currentPage ? 'disabled' : ''}>
            ${i}
          </button>
        `;
      }
    }
    
    // Show ellipsis before last page if needed
    if (this.currentPage < this.totalPages - 3 && this.totalPages > 6) {
      paginationHTML += `<span class="op-pagination-ellipsis">...</span>`;
    }
    
    // Always show last page (if more than 1 page total)
    if (this.totalPages > 1) {
      paginationHTML += `
        <button class="btn ${this.totalPages === this.currentPage ? 'btn--primary' : 'btn--secondary'}" 
                data-action="pagination" data-page="${this.totalPages}" ${this.totalPages === this.currentPage ? 'disabled' : ''}>
          ${this.totalPages}
        </button>
      `;
    }
    
    paginationHTML += `
        <button class="btn btn--secondary" data-action="pagination" data-page="next" ${this.currentPage === this.totalPages ? 'disabled' : ''}>
          Next
        </button>
      </div>
    `;
    
    paginationContainer.innerHTML = paginationHTML;
  }

  handlePaginationClick(event) {
    event.preventDefault();
    const page = event.target.getAttribute('data-page');
    
    if (page === 'prev' && this.currentPage > 1) {
      this.loadPage(this.currentPage - 1);
    } else if (page === 'next' && this.currentPage < this.totalPages) {
      this.loadPage(this.currentPage + 1);
    } else if (!isNaN(parseInt(page))) {
      this.loadPage(parseInt(page));
    }
  }

  scrollToTop() {
    // Small delay to ensure table content is fully rendered
    setTimeout(() => {
      // Smooth scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  }

  handleRemoveLine() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('.op-product-remove')) {
        event.preventDefault();
        const removeLink = event.target.closest('.op-product-remove');
        const row = event.target.closest('.op-table-row');
        
        if (row) {
          const sku = removeLink.getAttribute('data-sku');
          if (sku) {
            const skuIndex = this.allProductSkus.indexOf(sku);
            if (skuIndex > -1) {
              this.allProductSkus.splice(skuIndex, 1);
              delete this.allInputSkuQty[sku];
              
              // Recalculate pagination
              this.totalPages = Math.ceil(this.allProductSkus.length / this.itemsPerPage);
              
              // If current page is now empty and not the first page, go to previous page
              if (this.currentPage > this.totalPages && this.totalPages > 0) {
                this.loadPage(this.totalPages, false);
                return;
              }
              
              // If we still have products, reload current page
              if (this.allProductSkus.length > 0) {
                this.loadPage(this.currentPage, false);
                return;
              }
              
              // If no products left, hide the table
              const table = this.querySelector('#op-order-importer-table');
              if (table) {
                table.classList.add('hidden');
              }
              this.updatePaginationUI();
            }
          }
          
          row.remove();
        }
      }
    });
  }

  async handleAddToCart() {
    this.querySelectorAll('button[data-action="add-to-cart"]').forEach(button => {
      button.classList.add('is-loading');
    });

    const cartQtyMap = await this._fetchCartQtyMap();

    let formData = {
      'items': [],
      'sections': 'cart-icon-bubble'
    };

    const qtyAdjustments = [];

    this.querySelectorAll('.op-table-row .qty-input__input').forEach(input => {
      let variantId = input.getAttribute('data-vid');
      let available = input.getAttribute('data-available') == 'true';
      let originalQty = parseInt(input.value, 10) || 0;
      const row = input.closest('.op-table-row');
      const sku = row?.querySelector('.op-product-remove')?.getAttribute('data-sku') || '';
      const variant = this.productMap?.get(sku)?.variant;

      if (!available && sku) {
        const requestedQty = this.allInputSkuQty[sku] || originalQty;
        if (requestedQty > 0) {
          qtyAdjustments.push({ sku, barcode: variant?.barcode || '-', original: requestedQty, actual: 0 });
        }
        return;
      }

      if (variantId && originalQty > 0 && available) {
        let qtyToAdd = originalQty;
        const cartQty = cartQtyMap.get(String(variantId)) || 0;

        if (variant) {
          if (qtyToAdd < 1) {
            qtyToAdd = 1;
          }
          if (variant.quantityRule?.max) {
            const maxAllowed = variant.quantityRule.max - cartQty;
            if (qtyToAdd > maxAllowed) {
              qtyToAdd = maxAllowed;
            }
          }
          if (variant.quantityRule?.min && qtyToAdd < variant.quantityRule?.min) {
            qtyToAdd = variant.quantityRule?.min;
          }
          const remainingInventory = variant.inventoryQuantity - cartQty;
          if (variant.quantityRule?.min && remainingInventory < variant.quantityRule?.min && variant.inventoryManagement != null && variant.inventoryPolicy == 'deny') {
            qtyAdjustments.push({ sku, barcode: variant.barcode || '-', original: originalQty, actual: 0 });
            return;
          }
          if (qtyToAdd > remainingInventory && variant.inventoryManagement != null && variant.inventoryPolicy == 'deny') {
            qtyToAdd = remainingInventory;
          }
          if (variant.quantityRule?.increment && qtyToAdd % variant.quantityRule?.increment !== 0) {
            qtyToAdd = Math.floor(qtyToAdd / variant.quantityRule?.increment) * variant.quantityRule?.increment;
          }
        }

        if (qtyToAdd > 0) {
          if (qtyToAdd !== originalQty) {
            qtyAdjustments.push({ sku, barcode: variant?.barcode || '-', original: originalQty, actual: qtyToAdd });
          }
          formData.items.push({
            id: variantId,
            quantity: qtyToAdd
          });
        } else {
          qtyAdjustments.push({ sku, barcode: variant?.barcode || '-', original: originalQty, actual: 0 });
        }
      }
    });

    if (formData.items.length === 0) {
      this.querySelectorAll('button[data-action="add-to-cart"]').forEach(button => {
        button.classList.remove('is-loading');
      });
      if (qtyAdjustments.length > 0) {
        this._showQtyDiffPopup(qtyAdjustments);
      }
      return;
    }

    if (formData.items.length > 0) {
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      .then(response => {
        if (response.ok) {
          this.querySelectorAll('button[data-action="add-to-cart"]').forEach(button => {
            button.classList.add('is-success');
            setTimeout(() => {
              button.classList.remove('is-success');
              button.classList.remove('is-loading');
            }, 2000);
          });
          response.json().then(data => {
            if (data && data.sections && data.sections['cart-icon-bubble']) {
              const cartIconBubble = document.getElementById('cart-icon-bubble');
              if (cartIconBubble) {
                cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
              }
            }
          });
          if (qtyAdjustments.length) {
            this._showQtyDiffPopup(qtyAdjustments);
          }
        } else {
          this.querySelectorAll('button[data-action="add-to-cart"]').forEach(button => {
            button.classList.remove('is-loading');
          });
          response.json().then(data => {
            if (data && data.message) {
              this._showInfo(data.message, window.theme.strings.irPopup.errorTitle || 'Error');
            }
          });
        }
      })
      .catch((error) => {
        if (error) {
          this._showInfo(window.orderImporterStrings.an_error_occur || 'An error occurred while adding items to cart. Please try again later.');
        }
        this.querySelectorAll('button[data-action="add-to-cart"]').forEach(button => {
          button.classList.remove('is-loading');
        });
      })
    }
  }

  handleAddAllToCart(evt) {
    const opener = evt?.target?.closest?.('[data-action="add-all-to-cart"]') ?? null;
    if (!this.irPopup) return;

    const title = window.orderImporterStrings?.confirm_add_all_heading || 'Add all to cart?';
    const message = window.orderImporterStrings?.confirm_add_all_message || 'Are you sure you want to add all items to the cart? Unavailable items will be skipped.';
    const confirmLabel = window.orderImporterStrings?.add_all_to_cart || 'Add all to cart';
    const cancelLabel = window.orderImporterStrings?.confirm_cancel || 'Cancel';

    this.irPopup.show({
      type: 'confirm',
      title,
      message,
      confirmLabel,
      cancelLabel,
      onConfirm: () => this.doAddAllToCart()
    }, opener);
  }

  async doAddAllToCart() {
    const [cartQtyMap, ...fetchResults] = await Promise.all([
      this._fetchCartQtyMap(),
      ...(() => {
        const batches = [];
        for (let i = 0; i < this.allProductSkus.length; i += SEARCH_BATCH_SIZE) {
          batches.push(this.allProductSkus.slice(i, i + SEARCH_BATCH_SIZE));
        }
        return batches.map(batch => {
          const searchQuery = batch.map(sku => `variants.sku:${sku}`).join(' OR ');
          return fetch(`/search?q=${encodeURIComponent(searchQuery)}&type=product&view=order-importer&options[prefix]=none`);
        });
      })()
    ]);

    if (fetchResults.every(response => response.ok)) {
      const allResponses = await Promise.all(fetchResults.map(response => response.json()));
      const allVariants = allResponses
        .flat()
        .flatMap(productItem => {
          if (!OrderImporter.isOrderableInOp(productItem)) {
            return (productItem.variants || []).map(variant => ({ ...variant, available: false }));
          }
          return productItem.variants || [];
        });
      if (allVariants.length) {
        this.bulkAddToCart(allVariants, cartQtyMap);
      }
    } else {
      const msg = window.orderImporterStrings?.fetch_failed || 'Failed to fetch data for some items. Please try again later.';
      this._showInfo(msg, window.theme.strings.irPopup.errorTitle || 'Error');
    }
  }

  bulkAddToCart(variants, cartQtyMap) {
    let formData = {
      'items': [],
      'sections': 'cart-icon-bubble'
    };
    const qtyAdjustments = [];
    const processedSkus = new Set();

    variants.forEach(variant => {
      if (variant.available && variant.sku) {
        const originalQty = this.allInputSkuQty[variant.sku];
        if (!originalQty) return;
        processedSkus.add(variant.sku);
        let qtyToAdd = originalQty;
        const cartQty = cartQtyMap.get(String(variant.id)) || 0;
        if (qtyToAdd < 1) {
          qtyToAdd = 1;
        }
        if (variant.quantityRule?.max) {
          const maxAllowed = variant.quantityRule.max - cartQty;
          if (qtyToAdd > maxAllowed) {
            qtyToAdd = maxAllowed;
          }
        }
        if (variant.quantityRule?.min && qtyToAdd < variant.quantityRule?.min) {
          qtyToAdd = variant.quantityRule?.min;
        }
        const remainingInventory = variant.inventoryQuantity - cartQty;
        if (variant.quantityRule?.min && remainingInventory < variant.quantityRule?.min && variant.inventoryManagement != null && variant.inventoryPolicy == 'deny') {
          qtyAdjustments.push({ sku: variant.sku, barcode: variant.barcode || '-', original: originalQty, actual: 0 });
          return;
        }
        if (qtyToAdd > remainingInventory && variant.inventoryManagement != null && variant.inventoryPolicy == 'deny') {
          qtyToAdd = remainingInventory;
        }
        if (variant.quantityRule?.increment && qtyToAdd % variant.quantityRule?.increment !== 0) {
          qtyToAdd = Math.floor(qtyToAdd / variant.quantityRule?.increment) * variant.quantityRule?.increment;
        }
        if (qtyToAdd > 0) {
          if (qtyToAdd !== originalQty) {
            qtyAdjustments.push({ sku: variant.sku, barcode: variant.barcode || '-', original: originalQty, actual: qtyToAdd });
          }
          formData.items.push({
            id: variant.id,
            quantity: qtyToAdd
          });
        } else {
          qtyAdjustments.push({ sku: variant.sku, barcode: variant.barcode || '-', original: originalQty, actual: 0 });
        }
      } else if (variant.sku && this.allInputSkuQty[variant.sku]) {
        processedSkus.add(variant.sku);
        qtyAdjustments.push({ sku: variant.sku, barcode: variant.barcode || '-', original: this.allInputSkuQty[variant.sku], actual: 0 });
      }
    });

    this.allProductSkus.forEach(sku => {
      if (!processedSkus.has(sku) && this.allInputSkuQty[sku]) {
        qtyAdjustments.push({ sku, barcode: '-', original: this.allInputSkuQty[sku], actual: 0 });
      }
    });

    if (formData.items.length === 0 && qtyAdjustments.length > 0) {
      this._showQtyDiffPopup(qtyAdjustments);
      return;
    }

    if (formData.items.length > 0) {
      this.querySelectorAll('button[data-action="add-all-to-cart"]').forEach(button => {
        button.classList.add('is-loading');
      });
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      .then(response => {
        if (response.ok) {
          this.querySelectorAll('button[data-action="add-all-to-cart"]').forEach(button => {
            button.classList.add('is-success');
            setTimeout(() => {
              button.classList.remove('is-success');
              button.classList.remove('is-loading');
            }, 2000);
          });
          response.json().then(data => {
            if (data && data.sections && data.sections['cart-icon-bubble']) {
              const cartIconBubble = document.getElementById('cart-icon-bubble');
              if (cartIconBubble) {
                cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
              }
            }
          });
          if (qtyAdjustments.length) {
            this._showQtyDiffPopup(qtyAdjustments);
          }
        } else {
          this.querySelectorAll('button[data-action="add-all-to-cart"]').forEach(button => {
            button.classList.remove('is-loading');
          });
          response.json().then(data => {
            if (data && data.message) {
              this._showInfo(data.message, window.theme.strings.irPopup.errorTitle || 'Error');
            }
          });
        }
      })
      .catch((error) => {
        if (error) {
          this._showInfo(window.orderImporterStrings.an_error_occur || 'An error occurred while adding items to cart. Please try again later.');
        }
        this.querySelectorAll('button[data-action="add-all-to-cart"]').forEach(button => {
          button.classList.remove('is-loading');
        });
      })
    }
  }

  _showQtyDiffPopup(adjustments) {
    const s = window.orderImporterStrings || {};
    const title = s.qty_adjusted_title || 'Quantity adjusted';
    const headerBarcode = s.barcode || 'Barcode';
    const headerSku = s.sku || 'SKU';
    const headerRequested = s.qty_requested || 'Requested';
    const headerAdded = s.qty_added || 'Added';

    let html = `<p style="margin-bottom:12px">${s.qty_adjusted_message || 'The following items were added with a different quantity than originally requested:'}</p>`;
    html += `<div style="max-height:350px;overflow:auto">`;
    html += `<table style="width:100%;border-collapse:collapse;font-size:14px">`;
    html += `<thead><tr style="text-align:left;border-bottom:2px solid #ddd">
      <th style="padding:6px 8px">${headerSku}</th>
      <th style="padding:6px 8px">${headerBarcode}</th>
      <th style="padding:6px 8px;text-align:right">${headerRequested}</th>
      <th style="padding:6px 8px;text-align:right">${headerAdded}</th>
    </tr></thead><tbody>`;

    adjustments.forEach(a => {
      const highlight = a.actual === 0 ? 'color:#c00;font-weight:600' : 'color:#b45309';
      html += `<tr style="border-bottom:1px solid #eee">
        <td style="padding:6px 8px">${this._escapeText(a.sku)}</td>
        <td style="padding:6px 8px">${this._escapeText(a.barcode)}</td>
        <td style="padding:6px 8px;text-align:right">${a.original}</td>
        <td style="padding:6px 8px;text-align:right;${highlight}">${a.actual}</td>
      </tr>`;
    });

    html += `</tbody></table></div>`;

    if (this.irPopup?.show) {
      this.irPopup.show({ type: 'info', title, message: html, html: true });
    } else {
      let text = title + '\n\n';
      adjustments.forEach(a => {
        text += `${a.sku} (${a.barcode}): ${a.original} → ${a.actual}\n`;
      });
      alert(text);
    }
  }

  _escapeText(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }

  /**
   * In OP only products whose PIM metafield `item_type` (Varetype) matches
   * one of the values configured in the theme setting
   * `op_atc_allowed_varetypes` (exposed via `window.theme.settings.opAtcAllowedVaretypes`)
   * can be added to cart. Defaults to ["Lager", "Transit"] when the setting is missing.
   *
   * Additionally, when the theme setting `hide_atc_when_blocked_sale_ax`
   * (exposed via `window.theme.settings.hideAtcWhenBlockedSaleAx`) is enabled,
   * products are treated as not orderable when either the `pim.blocked_sale_ax`
   * or the `ax_integration.blocked_sale_ax` metafield is true. This mirrors the
   * logic in `snippets/op-atc-allowed.liquid` so the Order Importer cannot
   * bypass the block.
   *
   * @param {object} item - Product object returned by search.order-importer.liquid.
   * @returns {boolean} true when the product is orderable in OP.
   */
  static isOrderableInOp(item) {
    const varetype = (item?.metafields?.varetype || '').toString().trim().toLowerCase();
    if (!varetype) return false;

    const configured = window.theme?.settings?.opAtcAllowedVaretypes;
    const source = Array.isArray(configured) && configured.length ? configured : [];
    const allowed = source
      .map(value => (value || '').toString().trim().toLowerCase())
      .filter(Boolean);

    if (!allowed.includes(varetype)) return false;

    if (window.theme?.settings?.hideAtcWhenBlockedSaleAx) {
      const blockedSaleAx = (item?.metafields?.blockedSaleAx ?? '').toString().trim().toLowerCase();
      const axIntegrationBlockedSaleAx = (item?.metafields?.axIntegrationBlockedSaleAx ?? '').toString().trim().toLowerCase();
      if (blockedSaleAx === 'true' || axIntegrationBlockedSaleAx === 'true') return false;
    }

    return true;
  }

  async _fetchCartQtyMap() {
    const map = new Map();
    try {
      const res = await fetch(window.Shopify.routes.root + 'cart.js');
      if (res.ok) {
        const cart = await res.json();
        (cart.items || []).forEach(item => {
          const current = map.get(String(item.id)) || 0;
          map.set(String(item.id), current + item.quantity);
        });
      }
    } catch (_) { /* proceed without cart data */ }
    return map;
  }

  handleAddItemToCart(event) {
    event.preventDefault();
    const row = event.target.closest('.op-table-row');
    const button = event.target.closest('button[data-action="add-item-to-cart"]');
    if (!row) return;
    let qtyInput = row.querySelector('.qty-input__input');
    if (!qtyInput) return;

    let variantId = qtyInput.getAttribute('data-vid');
    let quantity = parseInt(qtyInput.value, 10);
    if (isNaN(quantity) || quantity <= 0) {
      this._showInfo(window.orderImporterStrings.please_enter_valid_quantity || 'Please enter a valid quantity.');
      return;
    }

    button.classList.add('is-loading');
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: quantity
        }],
        sections: 'cart-icon-bubble'
      })
    })
      .then(response => {
        if (response.ok) {
          button.classList.add('is-success');
          setTimeout(() => {
            button.classList.remove('is-success');
            button.classList.remove('is-loading');
          }, 2000);
          // update cart bubble from response
          response.json().then(data => {
            if (data && data.sections && data.sections['cart-icon-bubble']) {
              const cartIconBubble = document.getElementById('cart-icon-bubble');
              if (cartIconBubble) {
                cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
              }
            }
          });
        } else {
          button.classList.remove('is-loading');
          response.json().then(data => {
            if (data && data.message) {
              this._showInfo(data.message, window.theme.strings.irPopup.errorTitle || 'Error');
            }
          });
        }
      })
      .catch(error => {
        console.error('Error adding item to cart:', error);
        const msg = window.orderImporterStrings.an_error_occur || 'An error occurred while adding the item to the cart.';
        this._showInfo(msg, window.theme.strings.irPopup.errorTitle || 'Error');
        button.classList.remove('is-loading');
      });
  }
}

customElements.define('order-importer', OrderImporter);
